import React, { ReactElement } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { Event } from 'services/api';
import { MaterialSymbol } from '../../shared';
import { FeedButton } from './FeedButton';
import {
  FEED_COLORS,
  FEED_FONT,
  FEED_SHADOW,
  feedLocation,
  formatCheckedInAt,
  formatFeedTimeRange,
} from './feedTokens';

export type RailCardAction =
  | 'rsvp'
  | 'check-in'
  | 'check-in-attendees'
  | 'checked-in';

export interface EventCardRailProps {
  event: Event;
  hosting: boolean;
  desktop: boolean;
  loading?: boolean;
  onOpen: () => void;
  onAction: () => void;
}

const Flyer = ({ event }: { event: Event }): ReactElement => (
  <Box
    sx={{
      width: '60px',
      height: '80px',
      flexShrink: 0,
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: FEED_COLORS.placeholder,
      position: 'relative',
    }}
  >
    {event.flyerImageUrl && (
      <>
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${event.flyerImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(8px)',
            transform: 'scale(1.15)',
          }}
        />
        <Box
          component="img"
          src={event.flyerImageUrl}
          alt=""
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
      </>
    )}
  </Box>
);

const HostingChip = (): ReactElement => (
  <Box
    sx={{
      backgroundColor: FEED_COLORS.chipBg,
      borderRadius: '8px',
      px: '8px',
      py: '4px',
    }}
  >
    <Typography
      sx={{
        fontFamily: FEED_FONT,
        fontWeight: 500,
        fontSize: '12px',
        lineHeight: '16px',
        color: FEED_COLORS.textPrimary,
      }}
    >
      Hosting
    </Typography>
  </Box>
);

const railAction = (event: Event, hosting: boolean): RailCardAction => {
  if (hosting) return 'check-in-attendees';
  if (event.checkedIn) return 'checked-in';
  if (event.reserved) return 'check-in';
  return 'rsvp';
};

const GoingChip = (): ReactElement => (
  <Box
    sx={{
      backgroundColor: FEED_COLORS.successTint,
      borderRadius: '8px',
      px: '8px',
      py: '4px',
    }}
  >
    <Typography
      sx={{
        fontFamily: FEED_FONT,
        fontWeight: 500,
        fontSize: '12px',
        lineHeight: '16px',
        color: FEED_COLORS.success,
      }}
    >
      Going
    </Typography>
  </Box>
);

const StatusPill = ({
  desktop,
  label,
}: {
  desktop: boolean;
  label: string;
}): ReactElement => (
  <Box
    role="status"
    sx={{
      width: desktop ? '208px' : '100%',
      minHeight: desktop ? '44px' : '32px',
      borderRadius: '8px',
      backgroundColor: FEED_COLORS.successTint,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      px: desktop ? '24px' : '14px',
    }}
  >
    <MaterialSymbol
      name="check"
      size={desktop ? 18 : 16}
      color={FEED_COLORS.success}
    />
    <Typography
      sx={{
        fontFamily: FEED_FONT,
        fontWeight: 500,
        fontSize: desktop ? '14px' : '12px',
        lineHeight: desktop ? '22px' : '16px',
        color: FEED_COLORS.success,
      }}
    >
      {label}
    </Typography>
  </Box>
);

const RailCardCta = ({
  action,
  desktop,
  loading,
  checkedInAt,
  onAction,
}: {
  action: RailCardAction;
  desktop: boolean;
  loading: boolean;
  checkedInAt?: string;
  onAction: () => void;
}): ReactElement => {
  if (action === 'checked-in') {
    return (
      <StatusPill desktop={desktop} label={formatCheckedInAt(checkedInAt)} />
    );
  }

  const organizer = action === 'check-in-attendees';
  const checkIn = action === 'check-in';
  return (
    <FeedButton
      label={organizer ? 'Check in attendees' : checkIn ? 'Check in' : 'RSVP'}
      tone={organizer ? 'gold' : checkIn ? 'tertiary' : 'navy'}
      size={desktop ? 'default' : 'small'}
      fullWidth={!desktop}
      width={desktop && !organizer ? 208 : undefined}
      loading={loading}
      icon={
        organizer || checkIn ? (
          <MaterialSymbol
            name="qr_code_scanner"
            size={desktop ? 18 : 16}
            color={FEED_COLORS.textPrimary}
          />
        ) : undefined
      }
      onClick={onAction}
    />
  );
};

const MetaLine = ({
  desktop,
  children,
}: {
  desktop: boolean;
  children: string;
}): ReactElement => (
  <Typography
    sx={{
      m: 0,
      width: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontFamily: FEED_FONT,
      fontWeight: 400,
      fontSize: desktop ? '14px' : '12px',
      lineHeight: desktop ? '22px' : '18px',
      color: FEED_COLORS.textSecondary,
    }}
  >
    {children}
  </Typography>
);

const CardCopy = ({
  event,
  hosting,
  going,
  desktop,
  time,
  location,
}: {
  event: Event;
  hosting: boolean;
  going: boolean;
  desktop: boolean;
  time: string;
  location: string;
}): ReactElement => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '4px',
      minWidth: 0,
      flex: '1 1 auto',
    }}
  >
    {hosting && <HostingChip />}
    {going && <GoingChip />}
    <Typography
      component="p"
      sx={{
        m: 0,
        width: '100%',
        fontFamily: FEED_FONT,
        fontWeight: desktop ? 600 : 500,
        fontSize: desktop ? '16px' : '14px',
        lineHeight: '22px',
        color: FEED_COLORS.textPrimary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: desktop ? 1 : 2,
        WebkitBoxOrient: 'vertical',
      }}
    >
      {event.name}
    </Typography>
    {time && <MetaLine desktop={desktop}>{time}</MetaLine>}
    {location && <MetaLine desktop={desktop}>{location}</MetaLine>}
  </Box>
);

export const EventCardRail = ({
  event,
  hosting,
  desktop,
  loading = false,
  onOpen,
  onAction,
}: EventCardRailProps): ReactElement => {
  const location = feedLocation(event);
  const time = formatFeedTimeRange(event.startTime, event.endTime);
  const action = railAction(event, hosting);
  const going = !hosting && Boolean(event.reserved) && !event.checkedIn;

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: FEED_COLORS.surface,
        border: `1.5px solid ${FEED_COLORS.border}`,
        borderRadius: '16px',
        boxShadow: FEED_SHADOW,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: desktop ? 'row' : 'column',
        alignItems: desktop ? 'center' : 'stretch',
        gap: desktop ? '16px' : 0,
        p: desktop ? '16px' : 0,
      }}
    >
      <ButtonBase
        onClick={onOpen}
        disableRipple
        aria-label={event.name}
        sx={{
          display: 'flex',
          flex: desktop ? '1 1 auto' : 'none',
          flexDirection: 'row',
          alignItems: 'center',
          gap: desktop ? '16px' : '12px',
          p: desktop ? 0 : '12px',
          minWidth: 0,
          width: desktop ? 'auto' : '100%',
          textAlign: 'left',
        }}
      >
        <Flyer event={event} />
        <CardCopy
          event={event}
          hosting={hosting}
          going={going}
          desktop={desktop}
          time={time}
          location={location}
        />
      </ButtonBase>
      <Box sx={{ p: desktop ? 0 : '12px', pt: desktop ? 0 : 0, flexShrink: 0 }}>
        <RailCardCta
          action={action}
          desktop={desktop}
          loading={loading}
          checkedInAt={event.checkedInAt}
          onAction={onAction}
        />
      </Box>
    </Box>
  );
};

export const EventCardRailSkeleton = ({
  desktop,
}: {
  desktop: boolean;
}): ReactElement => (
  <Box
    sx={{
      width: '100%',
      height: desktop ? '112px' : '158px',
      borderRadius: '16px',
      backgroundColor: FEED_COLORS.chipBg,
    }}
  />
);
